import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  bumpMessageGroupListItem,
  normalizeGroupMessage,
  upsertMessageInPages,
} from "../actions/messageGroups.actions";
import { messageGroupsQueryKeys } from "../messageGroups.queryKeys";

export const useMessageGroupSocket = (socket, groupUid, companyId, onNewMessage, onGroupUpdated, onRead) => {
  const queryClient = useQueryClient();
  const processedMessageUidsRef = useRef(new Set());

  useEffect(() => {
    if (!socket || !groupUid || !companyId) return;

    const handleNewMessage = ({ groupUid: incomingUid, message }) => {
      if (incomingUid !== groupUid || !message) return;
      if (message.companyId != null && Number(message.companyId) !== Number(companyId)) return;

      const messageUid = message.uid;
      if (processedMessageUidsRef.current.has(messageUid)) return;
      processedMessageUidsRef.current.add(messageUid);

      const normalized = normalizeGroupMessage(message);

      queryClient.setQueryData(messageGroupsQueryKeys.messages(companyId, groupUid), (oldData) =>
        upsertMessageInPages(oldData, normalized),
      );
      onNewMessage?.(normalized);
    };

    const handleUpdated = ({ groupUid: incomingUid, change }) => {
      if (incomingUid !== groupUid) return;
      queryClient.invalidateQueries({ queryKey: messageGroupsQueryKeys.summary(companyId, groupUid) });
      onGroupUpdated?.(change);
    };

    const handleRead = ({ groupUid: incomingUid, reader, lastReadAt }) => {
      if (incomingUid !== groupUid) return;
      onRead?.(reader, lastReadAt);
    };

    socket.on("messageGroupMessage", handleNewMessage);
    socket.on("messageGroupUpdated", handleUpdated);
    socket.on("messageGroupRead", handleRead);

    return () => {
      socket.off("messageGroupMessage", handleNewMessage);
      socket.off("messageGroupUpdated", handleUpdated);
      socket.off("messageGroupRead", handleRead);
    };
  }, [socket, groupUid, companyId, queryClient, onNewMessage, onGroupUpdated, onRead]);
};

export const useMessageGroupsListSocket = (socket, activeCompanyId, openGroupUid = null) => {
  const queryClient = useQueryClient();
  const { uid, workforces } = useSelector((state) => state.userState);

  const identityRef = useRef({ uid, workforceUids: [] });
  identityRef.current = {
    uid,
    workforceUids: (workforces || []).map((workforce) => workforce.uid).filter(Boolean),
  };
  const openRef = useRef(openGroupUid);
  openRef.current = openGroupUid;
  const companyRef = useRef(activeCompanyId);
  companyRef.current = activeCompanyId;

  useEffect(() => {
    if (!socket || !activeCompanyId) return;

    const isFromMe = (sender) => {
      if (!sender) return false;
      const { uid: myUid, workforceUids } = identityRef.current;
      if (sender.type === "user" && sender.uid === myUid) return true;
      if (sender.type === "workforce" && sender.uid && workforceUids.includes(sender.uid)) return true;
      return false;
    };

    const mentionsMe = (mentions) => {
      if (!Array.isArray(mentions) || mentions.length === 0) return false;
      const { uid: myUid, workforceUids } = identityRef.current;
      return mentions.some(
        (mention) =>
          (mention.type === "user" && mention.uid === myUid) ||
          (mention.type === "workforce" && workforceUids.includes(mention.uid)),
      );
    };

    const handleMessage = ({ groupUid, message }) => {
      if (!groupUid || !message) return;
      if (message.companyId != null && Number(message.companyId) !== Number(companyRef.current)) return;
      if (message.kind === "system") {
        queryClient.invalidateQueries({ queryKey: messageGroupsQueryKeys.list(companyRef.current) });
        return;
      }

      const preview = message.content?.text ?? message.content?.name ?? null;
      const lastMessageAt = message.createdAt ?? new Date().toISOString();
      const isOpen = groupUid === openRef.current;
      const fromMe = isFromMe(message.sender);

      queryClient.setQueryData(messageGroupsQueryKeys.list(companyRef.current), (old) =>
        bumpMessageGroupListItem(old, groupUid, {
          lastMessage: preview,
          lastMessageAt,
          lastMessageSenderName: message.sender?.name ?? null,
          incrementUnread: !isOpen && !fromMe,
          mentionsMe: !isOpen && !fromMe && mentionsMe(message.mentions),
        }),
      );
    };

    const handleUpdated = () => {
      queryClient.invalidateQueries({ queryKey: messageGroupsQueryKeys.list(companyRef.current) });
    };

    socket.on("messageGroupMessage", handleMessage);
    socket.on("messageGroupUpdated", handleUpdated);

    return () => {
      socket.off("messageGroupMessage", handleMessage);
      socket.off("messageGroupUpdated", handleUpdated);
    };
  }, [socket, activeCompanyId, queryClient]);
};
